import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Globe2, Landmark, TowerControl, TrainFront, ChevronRight } from "lucide-react";
import api from "../lib/api";

/* IndianAirports.com — state landing page (SEO internal-linking hub).
   State intelligence from the captured IndiaS.com directory + every airport in the state. */
export default function IndiaStatePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setData(null); setErr(false);
    api.get(`/india/airports/states/${slug}`).then((r) => {
      setData(r.data);
      document.title = `Airports in ${r.data.state.name} — IndianAirports.com`;
    }).catch(() => setErr(true));
    window.scrollTo(0, 0);
  }, [slug]);

  if (err) return <div className="max-w-4xl mx-auto px-5 py-16 text-center text-sm text-zinc-500" data-testid="india-state-notfound">State not found. <Link to="/india/airports/directory" className="text-blue-700 font-700 hover:underline">Browse airports</Link></div>;
  if (!data) return <div className="max-w-4xl mx-auto px-5 py-12 text-sm text-zinc-500">Loading state…</div>;
  const s = data.state;

  return (
    <div data-testid="india-state-page">
      <div className="bg-gradient-to-br from-orange-50 via-white to-sky-50 border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-5 py-10">
          <Link to="/india/airports" className="text-xs font-700 text-blue-700 inline-flex items-center gap-1 hover:underline" data-testid="india-state-back"><ArrowLeft className="w-3.5 h-3.5" /> IndianAirports home</Link>
          <h1 className="mt-2 font-heading font-900 text-4xl sm:text-5xl tracking-tight" data-testid="india-state-name">Airports in {s.name}</h1>
          {s.tagline && <p className="mt-2 text-base text-zinc-600 max-w-2xl">{s.tagline}</p>}
          <div className="mt-4 flex flex-wrap gap-2 text-xs" data-testid="india-state-facts">
            {s.capital && <Fact label="Capital" value={s.capital} />}
            {s.region && <Fact label="Region" value={`${s.region} India`} />}
            {s.langs?.length > 0 && <Fact label="Languages" value={s.langs.join(", ")} />}
            {s.pop_cr && <Fact label="Population" value={`${s.pop_cr} crore`} />}
            <Fact label="Airports" value={data.airports.length} />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 py-8">
        {data.airports.length === 0 ? (
          <p className="text-sm text-zinc-500 py-8 text-center border border-dashed border-zinc-300 rounded-2xl">No airports in our directory for {s.name} yet — India's airport network is growing fast.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="india-state-airports">
            {data.airports.map((a) => (
              <Link key={a.iata} to={`/india/airports/${a.slug}`} data-testid={`india-state-airport-${a.iata}`}
                className="group bg-white border border-zinc-200 rounded-2xl overflow-hidden hover:border-blue-400 flex">
                <img src={a.image_url} alt="" className="w-28 object-cover shrink-0" loading="lazy" />
                <div className="p-3.5 min-w-0">
                  <div className="text-sm font-800 leading-snug group-hover:text-blue-700">{a.name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" /> {a.city} · <b className="font-mono">{a.iata}</b></div>
                  <div className="mt-1.5 flex items-center gap-2 text-[10px]">
                    <span className="font-800 uppercase bg-blue-50 text-blue-700 border border-blue-200 rounded px-1.5 py-0.5">{a.type.replace("_", " ")}</span>
                    {a.has_metro && <span className="inline-flex items-center gap-0.5 text-emerald-600 font-700"><TrainFront className="w-3 h-3" /> Metro</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {data.region_states.length > 0 && (
          <section className="mt-10" data-testid="india-state-region">
            <h2 className="font-heading font-800 text-base md:text-lg flex items-center gap-2"><Globe2 className="w-5 h-5 text-orange-600" /> More airports in {s.region} India</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {data.region_states.map((r) => (
                <Link key={r.slug} to={`/india/airports/states/${r.slug}`} data-testid={`india-state-sibling-${r.slug}`}
                  className="inline-flex items-center gap-1.5 text-xs font-700 bg-white border border-zinc-300 rounded-full px-3.5 py-2 hover:border-orange-400">
                  {r.name} <span className="text-zinc-400">· {r.airports}</span> <ChevronRight className="w-3 h-3 text-zinc-300" />
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="mt-10 bg-blue-700 text-white rounded-2xl p-5 flex flex-wrap items-center gap-4" data-testid="india-state-plan-cta">
          <Landmark className="w-6 h-6 shrink-0" />
          <div className="flex-1 min-w-[220px]">
            <div className="font-heading font-800">Planning a trip to {s.name}?</div>
            <p className="text-xs text-blue-100 mt-0.5">Build a full itinerary — flights, stays and day plans — with the TravelerPulse AI planner.</p>
          </div>
          <Link to="/plan-ai" className="bg-white text-blue-800 text-sm font-800 rounded-full px-5 py-2.5 hover:bg-blue-50">Plan a {s.name} trip</Link>
        </div>
        <p className="mt-6 text-[11px] text-zinc-400 flex items-center gap-1.5"><TowerControl className="w-3.5 h-3.5" /> State intelligence: {data.source.name}. Airport records carry their own source and last-verified dates.</p>
      </div>
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <span className="bg-white border border-zinc-200 rounded-full px-3 py-1.5">
      <span className="text-zinc-400 font-700 uppercase text-[9px] tracking-wide mr-1.5">{label}</span>
      <span className="font-700">{value}</span>
    </span>
  );
}
